import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import IKanbanBoardProductionSchemaProps from "../../../Components/KanbanBoardProduction/interfaces/kanban-board-production-schema.interface";
import { useToast } from "../../../Hooks/ToasContext";
import {
  IServiceOrderEntity,
  IServiceOrderProduct,
  IUpdateServiceOrdersPayload,
  ServiceOrderDueDateStatusEnum,
  ServiceOrderStatusEnum,
} from "../../../Services/api/service-orders/service-order.interface";
import { ListServiceOrdersUseCase } from "../../../Usecase/ServiceOrders/list-service-orders-usecase";
import { ShowServiceOrdersUseCase } from "../../../Usecase/ServiceOrders/show-service-orders-usecase";
import { UpdateServiceOrdersUseCase } from "../../../Usecase/ServiceOrders/update-service-orders-usecase";
import { Task } from "../../Financial/Details/financial-details.hook";
import { IListSearchPayload } from "../../../Components/List/list.types";

interface IUseListProductionProps {
  listServiceOrdersUseCase: ListServiceOrdersUseCase
  showServiceOrdersUseCase: ShowServiceOrdersUseCase
  updateServiceOrdersUseCase: UpdateServiceOrdersUseCase
}

const countDelayedProducts = (products: IServiceOrderProduct[] = []) =>
  products.filter((product) =>
    product.productionDueDateStatus === ServiceOrderDueDateStatusEnum.DELAYED ||
    product.productionDueDateStatus === ServiceOrderDueDateStatusEnum.EXPIRED
  ).length;

export function useListProduction({
  listServiceOrdersUseCase,
  showServiceOrdersUseCase,
}: IUseListProductionProps){
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [page, setPage] = useState(1)
    const [data, setData] = useState<IServiceOrderEntity[]>([])
    const [tabValue, setTabValue] = useState(0)
    const [productionTypeView, setProductionTypeView] = useState('0')
    const [totalRegisters, setTotalRegisters] = useState(0)
    const navigate = useNavigate()
    const [kanbanSchema, setKanbanSchema] = useState<IKanbanBoardProductionSchemaProps>()
    const [orderDetails, setOrderDetails] = useState<IServiceOrderEntity | undefined>()

    const { showToast } = useToast()

    const transformData = (dados: IServiceOrderEntity[]) => {
      const tasks: Record<string, Task> = {};
      const initialOS: string[] = [];
      const onProductionOS: string[] = [];
      const finishsedOS: string[] = [];

      dados.forEach((item) => {
          const taskId = item.id;
          tasks[taskId] = {
              id: taskId,
              content: {
                  os: `OS #${item.externalProductionId}`,
                  items: item.products
              }
          };

          if(item.status === ServiceOrderStatusEnum.INITIAL){
            initialOS.push(taskId);
          } else if (Object.entries(item.status)
            .filter(([value]) => ![ServiceOrderStatusEnum.ERROR, ServiceOrderStatusEnum.PRODUCED, ServiceOrderStatusEnum.BILLED].includes(value as ServiceOrderStatusEnum))){
            onProductionOS.push(taskId)
          } else if (Object.entries(item.status)
            .filter(([value]) => [ServiceOrderStatusEnum.PRODUCED, ServiceOrderStatusEnum.BILLED].includes(value as ServiceOrderStatusEnum))){
            finishsedOS.push(taskId)
          }
      });

      return {
          tasks,
          columns: {
            [ServiceOrderStatusEnum.INITIAL]: {
                  id: ServiceOrderStatusEnum.INITIAL,
                  title: 'Aguardando Início',
                  taskIds: initialOS,
              },
              [ServiceOrderStatusEnum.PROCESSING]: {
                  id: ServiceOrderStatusEnum.PROCESSING,
                  title: 'Em Produção',
                  taskIds: onProductionOS,
              },
              [ServiceOrderStatusEnum.PRODUCED]: {
                  id: ServiceOrderStatusEnum.PRODUCED,
                  title: 'Produzido',
                  taskIds: finishsedOS,
              },
          },
          columnOrder: [ServiceOrderStatusEnum.INITIAL, ServiceOrderStatusEnum.PROCESSING, ServiceOrderStatusEnum.PRODUCED],
      };
    }

    const listProduction = useCallback(async ({ page: currentPage }: IListSearchPayload) => {
        try {
          setLoading(true)
          setLoadError(false)

          const listResult = await listServiceOrdersUseCase.handle(currentPage);

          if (listResult.isFailure) {
            setLoadError(true)
            showToast(String(listResult?.error?.error), 'error');
            return;
          }

         const list = listResult.getValue()
         const rows = list?.data ?? []

         setKanbanSchema(transformData(rows))
         setTotalRegisters(list?.total ?? rows.length)
         setData(rows)
         setLoadError(false)
         setPage(currentPage)
        } catch (error) {
          setLoadError(true)
          console.error("error listProduction function: ", error)
        } finally {
          setLoading(false);
        }
      }, [listServiceOrdersUseCase, showToast]);

    const handlePageChange = useCallback((payload: IListSearchPayload) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      listProduction({ page: payload.page })
    }, [listProduction]);

    const list = useMemo(() => {
        return data?.map((order) => {
            const products = order.products ?? []
            const delayedProductsCount = countDelayedProducts(products)

            return {
                ...order,
                productsCount: products.length,
                delayedProductsCount,
                primaryProductName: products[0]?.product?.name ?? '',
            }
        }) ?? []
    }, [data])

    const handleShowDetails = (code: string) => {
      console.log("code: ", code)
    }

    const handleConfirmProductionItems = async (_e: IUpdateServiceOrdersPayload) => {
      try {
        setLoading(false)
      } catch (error) {
        console.log("erro handleConfirmProductionItems: ", error)
      } finally {
        setLoading(false);
      }
    }

    const handleShowOrderDetails = async (osId?: string) => {
      try {
        setLoading(true)

        const listResult = await showServiceOrdersUseCase.handle(String(osId))

        if (listResult.isFailure) {
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

       const dados = listResult.getValue()

       if(dados){
        setOrderDetails(dados)
       }
      } catch (error) {
        console.log("error listProduction function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
    };

    const handleNavigateDetails = (e: IServiceOrderEntity) => {
      navigate(`/production-details/${e.id}`)
    }

    return {
        loading,
        loadError,
        listProduction,
        list,
        data,
        handleShowDetails,
        setOrderDetails,
        handleConfirmProductionItems,
        kanbanSchema,
        handleShowOrderDetails,
        orderDetails,
        tabValue,
        handleChangeTab,
        setProductionTypeView,
        productionTypeView,
        totalRegisters,
        page,
        handlePageChange,
        handleNavigateDetails,
    }
}
